import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Linkedin, MessageCircle, Link2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import './ShareButtons.css';

const ShareButtons = ({ 
  url = window.location.href, 
  title = "Debatefy - Learn Debating with AI",
  description = "AI-powered debate platform with real-time fact-checking. Join the conversation!",
  hashtags = "Debatefy,AI,FactChecking,CriticalThinking"
}) => {
  const [showButtons, setShowButtons] = useState(false);
  const [copied, setCopied] = useState(false);

  // Encode URL components for sharing
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&via=debatefy&hashtags=${hashtags}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    toast.success(`Sharing on ${platform.charAt(0).toUpperCase() + platform.slice(1)}!`);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url,
        });
        toast.success('Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      setShowButtons(!showButtons);
    }
  };

  return (
    <div className="share-container">
      <Button
        variant="outline"
        onClick={handleNativeShare}
        className="share-main-button"
      >
        <Share2 size={18} />
        <span>Share</span>
      </Button>

      {showButtons && (
        <div className="share-buttons-popup">
          <button
            onClick={() => handleShare('facebook')}
            className="share-button facebook"
            aria-label="Share on Facebook"
          >
            <Facebook size={20} />
            <span>Facebook</span>
          </button>

          <button
            onClick={() => handleShare('twitter')}
            className="share-button twitter"
            aria-label="Share on Twitter"
          >
            <Twitter size={20} />
            <span>Twitter</span>
          </button>

          <button
            onClick={() => handleShare('linkedin')}
            className="share-button linkedin"
            aria-label="Share on LinkedIn"
          >
            <Linkedin size={20} />
            <span>LinkedIn</span>
          </button>

          <button
            onClick={() => handleShare('whatsapp')}
            className="share-button whatsapp"
            aria-label="Share on WhatsApp"
          >
            <MessageCircle size={20} />
            <span>WhatsApp</span>
          </button>

          <button
            onClick={handleCopyLink}
            className="share-button copy-link"
            aria-label="Copy link"
          >
            {copied ? <Check size={20} /> : <Link2 size={20} />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButtons;
